import React, { useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import useRadioFeedbackDraft from './useRadioFeedbackDraft';
import {
  FEEDBACK_FIELDS,
  RADIO_FEEDBACK_MIN_SECONDS,
  STANDOUT_OPTIONS,
  formatRadioFeedbackTime,
  getFeedbackUnlockProgress,
  getRadioFeedbackArtistName,
  getRadioFeedbackTrackId,
  getRadioFeedbackTrackTitle,
  getRewardMessage,
  isFeedbackComplete,
  isFeedbackUnlocked,
} from './hitLabRadioFeedback';

function FeedbackChoiceGroup({ field, value, onChange, disabled }) {
  return (
    <fieldset className="hitlab-radio-feedback-group" disabled={disabled}>
      <legend>{field.label}</legend>
      <div role="radiogroup" aria-label={field.label}>
        {field.options.map(([optionValue, label]) => (
          <label
            key={optionValue}
            className={`hitlab-radio-feedback-choice ${
              value === optionValue ? 'is-selected' : ''
            }`}
          >
            <input
              type="radio"
              name={field.name}
              value={optionValue}
              checked={value === optionValue}
              onChange={() => onChange(field.name, optionValue)}
            />
            <span>{label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function FeedbackPlayerSession({
  open = false,
  track = null,
  player = null,
  elapsed = 0,
  duration = 0,
  source = 'joe_galaxy_radio',
  route = '/hitlab/radio',
  loginHref = '/login?from=%2Fhitlab%2Fradio',
  variant = 'full',
  triggerRef,
  onClose,
  onLogin,
  onSubmitted,
}) {
  const dialogRef = useRef(null);
  const initialFocusDoneRef = useRef(false);
  const {
    track: reviewTrack,
    form: feedbackForm,
    evidence,
    submitting: feedbackSubmitting,
    result: feedbackResult,
    error: feedbackError,
    authRequired,
    updateForm: updateFeedbackForm,
    submit: submitFeedback,
  } = useRadioFeedbackDraft({
    open, track, player, elapsed, duration, source, route, onSubmitted,
  });

  const trackId = getRadioFeedbackTrackId(reviewTrack);
  const trackTitle = getRadioFeedbackTrackTitle(reviewTrack);
  const artistName = getRadioFeedbackArtistName(reviewTrack);
  const coverArtUrl = String(reviewTrack?.coverArtUrl || '').trim();
  const feedbackEligible = Boolean(trackId) && reviewTrack?.feedbackEligible !== false;
  const feedbackUnlocked = isFeedbackUnlocked(evidence);
  const feedbackUnlockProgress = getFeedbackUnlockProgress(evidence);
  const complete = isFeedbackComplete(feedbackForm);

  const closeSheet = useCallback(() => {
    onClose?.();
    window.setTimeout(() => {
      triggerRef?.current?.focus?.();
    }, 0);
  }, [onClose, triggerRef]);

  useEffect(() => {
    if (!open || !feedbackEligible) {
      initialFocusDoneRef.current = false;
      return undefined;
    }
    if (initialFocusDoneRef.current) return undefined;
    initialFocusDoneRef.current = true;
    const focusTimer = window.setTimeout(() => {
      const dialog = dialogRef.current;
      if (!dialog) return;
      if (dialog.contains(document.activeElement)) return;
      dialog.focus();
    }, 0);
    return () => window.clearTimeout(focusTimer);
  }, [feedbackEligible, open]);

  useEffect(() => {
    if (!open || !feedbackEligible) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeSheet();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeSheet, feedbackEligible, open]);

  if (!open || !feedbackEligible) return null;

  const backdropClassName = [
    'hitlab-radio-feedback-backdrop',
    variant === 'dock' ? 'hitlab-radio-feedback-backdrop--dock' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const sheetClassName = [
    'hitlab-radio-feedback-sheet',
    variant === 'dock' ? 'hitlab-radio-feedback-sheet--dock' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={backdropClassName}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) closeSheet();
      }}
    >
      <section
        className={sheetClassName}
        role="dialog"
        aria-modal="true"
        aria-labelledby="hitlab-radio-feedback-title"
        tabIndex="-1"
        ref={dialogRef}
      >
        <button
          type="button"
          className="hitlab-radio-feedback-close"
          onClick={closeSheet}
          aria-label="Close feedback"
        >
          Close
        </button>

        <div className="hitlab-radio-feedback-track">
          {coverArtUrl ? (
            <img
              src={coverArtUrl}
              alt={`Cover art for ${trackTitle} by ${artistName}`}
              width="96"
              height="96"
            />
          ) : (
            <div className="hitlab-radio-feedback-art-fallback" aria-hidden="true">
              JG
            </div>
          )}
          <div>
            <p className="hitlab-kicker">Fan action signal</p>
            <h2 id="hitlab-radio-feedback-title">
              Share your honest feedback.
            </h2>
            {player?.token && feedbackUnlocked && Number.isFinite(reviewTrack?.feedbackRewardJgk) &&
              reviewTrack.feedbackRewardJgk > 0 && !feedbackResult &&
              <p>Eligible feedback earns {reviewTrack.feedbackRewardJgk} JGK after validation.</p>}
            <p>
              {trackTitle} by {artistName}
            </p>
          </div>
        </div>

        {!player?.token ? (
          <div className="hitlab-radio-feedback-auth">
            <h3>Sign in to share your honest feedback.</h3>
            <p>
              Tell the artist what connected with you. Feedback and voluntary support do not buy selection.
            </p>
            <button type="button" className="hitlab-primary-btn" onClick={onLogin}>
              Sign In
            </button>
            <Link className="hitlab-secondary-link" to={loginHref}>
              Continue to login
            </Link>
          </div>
        ) : !feedbackUnlocked ? (
          <div className="hitlab-radio-feedback-locked">
            <h3>Listen a little longer to unlock rewarded feedback.</h3>
            <p>
              {formatRadioFeedbackTime(evidence.elapsed)} /{' '}
              {formatRadioFeedbackTime(RADIO_FEEDBACK_MIN_SECONDS)}
            </p>
            <div
              className="hitlab-radio-feedback-unlock"
              role="progressbar"
              aria-label="Rewarded feedback unlock progress"
              aria-valuemin="0"
              aria-valuemax="100"
              aria-valuenow={Math.round(feedbackUnlockProgress)}
            >
              <span style={{ width: `${feedbackUnlockProgress}%` }} />
            </div>
          </div>
        ) : feedbackResult ? (
          <div className="hitlab-radio-feedback-result" role="status">
            <h3>{getRewardMessage(feedbackResult)}</h3>
            <p>
              Your signal helps records earn their next move inside Joe Galaxy
              Radio.
            </p>
          </div>
        ) : (
          <form
            className="hitlab-radio-feedback-form"
            onSubmit={(event) => {
              event.preventDefault();
              void submitFeedback();
            }}
          >
            <p>
              Your signal helps records earn their next move inside Joe Galaxy
              Radio.
            </p>
            {FEEDBACK_FIELDS.map((field) => (
              <FeedbackChoiceGroup
                key={field.name}
                field={field}
                value={feedbackForm[field.name]}
                onChange={updateFeedbackForm}
                disabled={feedbackSubmitting}
              />
            ))}

            <details className="hitlab-radio-feedback-extra">
              <summary>Add optional detail</summary>
              <fieldset className="hitlab-radio-feedback-group" disabled={feedbackSubmitting}>
                <legend>What stood out?</legend>
                <div role="radiogroup" aria-label="What stood out?">
                  {STANDOUT_OPTIONS.map(([optionValue, label]) => (
                    <label
                      key={optionValue}
                      className={`hitlab-radio-feedback-choice ${
                        feedbackForm.standoutReason === optionValue
                          ? 'is-selected'
                          : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name="standoutReason"
                        value={optionValue}
                        checked={feedbackForm.standoutReason === optionValue}
                        onChange={() =>
                          updateFeedbackForm('standoutReason', optionValue)
                        }
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
              <label className="hitlab-radio-feedback-comment">
                Optional comment
                <textarea
                  value={feedbackForm.comment}
                  maxLength="500"
                  disabled={feedbackSubmitting}
                  onChange={(event) =>
                    updateFeedbackForm('comment', event.target.value)
                  }
                  placeholder="A short note for the signal."
                />
              </label>
            </details>

            {feedbackError ? (
              <div className="hitlab-radio-error" role="alert">
                {feedbackError}
                {authRequired ? (
                  <button type="button" className="hitlab-secondary-link" onClick={onLogin}>
                    Sign in again
                  </button>
                ) : null}
              </div>
            ) : null}

            <button
              type="submit"
              className="hitlab-primary-btn"
              disabled={!complete || feedbackSubmitting}
            >
              {feedbackSubmitting ? 'Submitting' : 'Submit feedback'}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}

export default function HitLabRadioFeedbackSheet(props) {
  const identity =
    props.player?.data?.player?._id || props.player?._id || props.player?.token;
  const sessionKey = props.player?.token ? `player:${identity}` : 'guest';
  // Changing account/logout unmounts private drafts and invalidates pending UI work.
  return <FeedbackPlayerSession key={sessionKey} {...props} />;
}
